// FUNCTIONALITY-101 / CROSSSELL-109 / 114 / VISUAL-SEARCH-001-R6 — Store V2 home.
// Photo search lives in global Chrome header (not Home-only).
// COMMERCE-176B-R2-R2 — promo strip AFTER categories (lower Home slot), not before nav.
import { memo, useCallback, useEffect, useMemo, useState, type ReactNode } from "react";
import type { CatalogV2Meta } from "./catalog-v2-types.js";
import { mergeStoreTilesFromApi, type StoreV2Tile } from "./taxonomy-v2.js";
import type { StoreV2Lang } from "./store-v2-locale.js";
import { IsolatedSearchBar } from "./IsolatedSearchBar.js";
import { HomePromoStripV2 } from "./HomePromoStripV2.js";
import { t } from "./store-v2-ui-copy.js";
import type { SearchableProduct } from "./search-v2.js";

function ukVocative(name: string): string {
  const n = name.trim();
  if (!n) return "";
  if (/ія$/i.test(n)) return `${n.slice(0, -1)}є`;
  if (/я$/i.test(n)) return `${n.slice(0, -1)}е`;
  if (/а$/i.test(n)) return `${n.slice(0, -1)}о`;
  if (/й$/i.test(n)) return `${n.slice(0, -1)}ю`;
  if (/[еєиіоу]$/i.test(n)) return n;
  return `${n}е`;
}

function sessionGreeting(lang: StoreV2Lang, welcomeName: string | null | undefined, sessionKey: string): string {
  let h = 0;
  const seed = sessionKey || "s";
  for (let i = 0; i < seed.length; i += 1) h = (h * 31 + seed.charCodeAt(i)) >>> 0;
  const idx = h % 3;
  if (lang === "en") {
    const n = welcomeName?.trim() || "";
    const rows = n
      ? [`${n}, have a good day 🌸`, `${n}, time to find something tasty 🍜`, `${n}, may today taste great 🥢`]
      : ["Have a good day 🌸", "Time to find something tasty 🍜", "May today taste great 🥢"];
    return rows[idx]!;
  }
  const n = welcomeName?.trim() ? ukVocative(welcomeName.trim()) : "";
  const rows = n
    ? [`🍜 ${n}, гарного дня 🌸`, `🍜 ${n}, час знайти щось смачне 🍜`, `🍜 ${n}, нехай сьогодні буде смачно 🥢`]
    : ["🍜 Гарного дня 🌸", "🍜 Час знайти щось смачне 🍜", "🍜 Нехай сьогодні буде смачно 🥢"];
  return rows[idx]!;
}

export interface HomeV2Props {
  lang: StoreV2Lang;
  meta: CatalogV2Meta;
  showWelcomeBack?: boolean;
  welcomeName?: string | null;
  sessionKey: string;
  /** R7 — photo search beside Home text search (same shell as list pages). */
  visualSearchSlot?: ReactNode;
  /** 168D5 — local catalog for 2-char autocomplete (same search engine). */
  searchableProducts?: SearchableProduct[];
  /** SEARCH-PERF-169A — bump to force-close suggestion overlay. */
  suggestCloseToken?: number;
  /** R4A — remount empty draft (new search session). */
  searchBarEpoch?: number;
  onOpenCategory: (categoryId: string) => void;
  onPrefetchCategory?: (categoryId: string) => void;
  onSearch: (query: string) => void;
  onOpenProduct: (productId: string) => void;
  onOpenBrand?: (brandName: string) => void;
  onOpenCategoryLabel?: (categoryName: string) => void;
  onOpenProfile: () => void;
  onOpenNew: () => void;
  onOpenBrands: () => void;
  onPrefetchBrands?: () => void;
  onOpenCountries: () => void;
  onPrefetchCountries?: () => void;
  onOpenDropshipping: () => void;
  onOpenPromotions: () => void;
  emitEvent: (name: string, payload: Record<string, unknown>) => void;
  /** GROWTH-R1 — optional contextual recovery card (unpaid / checkout / cart). */
  recoverySlot?: ReactNode;
  /** R7.1 — lazy on-site recommendations (fail-open). */
  onsiteRecSlot?: ReactNode;
}

const HomeCategoryTiles = memo(function HomeCategoryTiles({
  lang,
  tiles,
  countById,
  onOpenCategory,
  onPrefetchCategory,
  onOpenPromotions,
}: {
  lang: StoreV2Lang;
  tiles: StoreV2Tile[];
  countById: Map<string, number>;
  onOpenCategory: (categoryId: string) => void;
  onPrefetchCategory?: (categoryId: string) => void;
  onOpenPromotions: () => void;
}) {
  return (
    <div className="sv2-tiles">
      <button
        type="button"
        className="sv2-tile sv2-tile-promo"
        data-testid="sv2-tile-promotions"
        onClick={onOpenPromotions}
      >
        <span className="sv2-tile-emoji">🔥</span>
        <span className="sv2-tile-name">{lang === "en" ? "Deals" : "Акції"}</span>
      </button>
      {tiles.map((tile) => {
        const count = countById.get(tile.id) ?? 0;
        return (
          <button
            key={tile.id}
            type="button"
            className="sv2-tile"
            data-testid={tile.id.startsWith("mgr_cat_") ? `sv2-tile-dynamic-${tile.id}` : undefined}
            onPointerDown={() => onPrefetchCategory?.(tile.id)}
            onClick={() => onOpenCategory(tile.id)}
          >
            <span className="sv2-tile-emoji">{tile.icon}</span>
            <span className="sv2-tile-name">{lang === "en" ? tile.nameEn : tile.name}</span>
            {count > 0 ? <span className="sv2-tile-count">{count}</span> : null}
          </button>
        );
      })}
    </div>
  );
});

export function HomeV2({
  lang,
  meta,
  showWelcomeBack,
  welcomeName,
  sessionKey,
  visualSearchSlot,
  searchableProducts,
  suggestCloseToken = 0,
  searchBarEpoch = 0,
  onOpenCategory,
  onPrefetchCategory,
  onSearch,
  onOpenProduct,
  onOpenBrand,
  onOpenCategoryLabel,
  onOpenProfile,
  onOpenNew,
  onOpenBrands,
  onPrefetchBrands,
  onOpenCountries,
  onPrefetchCountries,
  onOpenDropshipping,
  onOpenPromotions,
  emitEvent,
  recoverySlot,
  onsiteRecSlot,
}: HomeV2Props) {
  void showWelcomeBack;
  void searchableProducts;

  const [apiTiles, setApiTiles] = useState<StoreV2Tile[] | null>(null);

  const countById = useMemo(() => {
    const m = new Map<string, number>();
    for (const c of meta.categories ?? []) {
      if (c?.id) m.set(String(c.id), Number(c.count) || 0);
    }
    return m;
  }, [meta.categories]);

  const tiles = useMemo(() => {
    if (apiTiles?.length) return apiTiles;
    return mergeStoreTilesFromApi(meta.categories ?? []);
  }, [apiTiles, meta.categories]);

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        const { fetchCatalogCategories } = await import("../api/client.js");
        const res = await fetchCatalogCategories();
        if (cancelled) return;
        const cats = Array.isArray(res.categories) ? res.categories : [];
        setApiTiles(
          mergeStoreTilesFromApi(
            cats.map((c) => {
              const row = c as { id?: string; label?: string; name?: string; emoji?: string | null; sort?: number };
              const sortN = Number(row.sort);
              return {
                id: String(row.id ?? ""),
                label: String(row.label ?? row.name ?? ""),
                name: String(row.name ?? row.label ?? ""),
                emoji: row.emoji ?? null,
                ...(Number.isFinite(sortN) ? { sort: sortN } : {}),
              };
            }),
          ),
        );
      } catch {
        /* keep baseline STORE_V2_TILES via mergeStoreTilesFromApi(meta) */
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const submitSearch = useCallback(
    (q: string) => {
      onSearch(q);
    },
    [onSearch],
  );

  const onPerf = useCallback(
    (metric: string, ms: number, extra?: Record<string, number>) => {
      emitEvent("search_bar_perf", { metric, ms, ...(extra ?? {}) });
    },
    [emitEvent],
  );

  const greeting = useMemo(
    () => sessionGreeting(lang, welcomeName, sessionKey),
    [lang, welcomeName, sessionKey],
  );

  return (
    <div className="sv2-body" data-testid="sv2-home">
      <p className="sv2-home-greeting" data-testid="sv2-home-greeting">
        <span className="sv2-home-greeting-bowl" aria-hidden="true">
          🍜
        </span>
        {greeting}
      </p>

      {recoverySlot ?? null}

      <div className="sv2-home-promos" data-testid="sv2-home-nav-tiles">
        <button
          type="button"
          className="sv2-mini-promo"
          onClick={() => {
            emitEvent("cta_click", { target_key: "home.new_arrivals" });
            onOpenNew();
          }}
          data-testid="sv2-home-tile-new"
        >
          <span className="sv2-mini-promo-icon" aria-hidden="true">🆕</span>
          <strong>{t(lang, "new_arrivals")}</strong>
          <span>{t(lang, "new_arrivals_hint")}</span>
        </button>
        <button
          type="button"
          className="sv2-mini-promo"
          onPointerDown={() => onPrefetchBrands?.()}
          onClick={() => {
            emitEvent("cta_click", { target_key: "home.brands" });
            onOpenBrands();
          }}
          data-testid="sv2-home-tile-brands"
        >
          <span className="sv2-mini-promo-icon" aria-hidden="true">🏷️</span>
          <strong>{t(lang, "brands")}</strong>
          <span>{t(lang, "brands_hint")}</span>
        </button>
        <button
          type="button"
          className="sv2-mini-promo sv2-home-countries"
          onPointerDown={() => onPrefetchCountries?.()}
          onClick={() => {
            emitEvent("cta_click", { target_key: "home.countries" });
            onOpenCountries();
          }}
          data-testid="sv2-home-tile-countries"
        >
          <span className="sv2-mini-promo-icon" aria-hidden="true">🌏</span>
          <strong>{t(lang, "countries")}</strong>
          <span>{t(lang, "countries_hint")}</span>
        </button>
        <button
          type="button"
          className="sv2-mini-promo"
          onClick={() => {
            emitEvent("cta_click", { target_key: "home.dropshipping" });
            onOpenDropshipping();
          }}
          data-testid="sv2-home-tile-dropshipping"
        >
          <span className="sv2-mini-promo-icon" aria-hidden="true">🚚</span>
          <strong>{t(lang, "dropshipping")}</strong>
          <span>{t(lang, "dropshipping_hint")}</span>
        </button>
      </div>

      <div className="sv2-sticky-tools">
        <IsolatedSearchBar
          lang={lang}
          onSubmit={submitSearch}
          onOpenProduct={onOpenProduct}
          {...(onOpenBrand ? { onOpenBrand } : {})}
          {...(onOpenCategoryLabel ? { onOpenCategoryLabel } : {})}
          onPerf={onPerf}
          visualSlot={visualSearchSlot}
          forceCloseToken={suggestCloseToken}
          sessionEpoch={searchBarEpoch}
        />
      </div>

      <div className="sv2-section-title" style={{ marginTop: 16 }}>
        {lang === "en" ? "Categories" : "Категорії"}
      </div>
      <HomeCategoryTiles
        lang={lang}
        tiles={tiles}
        countById={countById}
        onOpenCategory={(id) => {
          emitEvent("cta_click", { target_key: `home.category.${id}` });
          onOpenCategory(id);
        }}
        {...(onPrefetchCategory ? { onPrefetchCategory } : {})}
        onOpenPromotions={onOpenPromotions}
      />

      <HomePromoStripV2
        lang={lang}
        sessionKey={sessionKey}
        onOpenProduct={onOpenProduct}
        onOpenAllPromotions={onOpenPromotions}
      />

      {onsiteRecSlot}

      <button type="button" className="sv2-about-link" onClick={onOpenProfile}>
        {lang === "en" ? "About & legal" : "Про магазин і документи"}
      </button>
    </div>
  );
}
